import { getUser } from './auth.js';

// Role capabilities mapping - aligned with backend roles
const ROLE_CAPABILITIES = {
  superadmin: {
    canManageUsers: true,
    canManageCompanies: true,
    canViewAllOrders: true,
    canApproveOrders: true,
    canRejectOrders: true,
    canCreateCirculars: true,
    canDeleteCirculars: true,
    canViewAuditLogs: true,
    canManageReminders: true,
    canViewAllKPIs: true,
    canUploadDocuments: true,
    canDeleteDocuments: true,
    canViewDocumentStats: true,
    canUploadForms: true,
    canManageForms: true,
    canManageCategories: true,
  },
  admin: {
    canManageUsers: true,
    canManageCompanies: true,
    canViewAllOrders: true,
    canApproveOrders: true,
    canRejectOrders: true,
    canCreateCirculars: true,
    canDeleteCirculars: true,
    canViewAuditLogs: true,
    canManageReminders: true,
    canViewAllKPIs: true,
    canUploadDocuments: true,
    canDeleteDocuments: true,
    canViewDocumentStats: true,
    canUploadForms: true,
    canManageForms: true,
    canManageCategories: true,
  },
  viewer: {
    canManageUsers: false,
    canManageCompanies: false,
    canViewAllOrders: false,
    canApproveOrders: false,
    canRejectOrders: false,
    canCreateCirculars: false,
    canDeleteCirculars: false,
    canViewAuditLogs: false,
    canManageReminders: false,
    canViewAllKPIs: false,
    canUploadDocuments: true,
    canDeleteDocuments: false,
    canViewDocumentStats: false,
    canUploadForms: true,
    canManageForms: true,
    canManageCategories: true,
  },
};

// Get current user's capabilities
export const getUserCapabilities = () => {
  const user = getUser();
  if (!user) {
    return ROLE_CAPABILITIES.viewer;
  }
  
  // Map role_name to role for compatibility
  const role = user.role || user.role_name;
  if (!role) {
    return ROLE_CAPABILITIES.viewer;
  }
  
  return ROLE_CAPABILITIES[role] || ROLE_CAPABILITIES.viewer;
};

// Check if user has specific capability
export const hasCapability = (capability) => {
  const capabilities = getUserCapabilities();
  return capabilities[capability] || false;
};

// Check if user can perform action on resource
export const canPerform = (action, resource) => {
  const user = getUser();
  if (!user) return false;
  
  const capabilities = getUserCapabilities();
  
  switch (action) {
    case 'create':
      return capabilities[`canCreate${resource}`] || false;
    case 'read':
      return capabilities[`canView${resource}`] || false;
    case 'update':
      return capabilities[`canUpdate${resource}`] || false;
    case 'delete':
      return capabilities[`canDelete${resource}`] || false;
    case 'approve':
      return capabilities[`canApprove${resource}`] || false;
    case 'reject':
      return capabilities[`canReject${resource}`] || false;
    default:
      return false;
  }
};

// Check if user can view all data or only their own
export const canViewAll = (resource) => {
  const capabilities = getUserCapabilities();
  return capabilities[`canViewAll${resource}`] || false;
};

// Get user's role
export const getUserRole = () => {
  const user = getUser();
  return user?.role || user?.role_name || 'viewer';
};

// Check if user is admin
export const isAdmin = () => {
  return getUserRole() === 'admin';
};

// Check if user is superadmin
export const isSuperAdmin = () => {
  return getUserRole() === 'superadmin';
};

// Check if user is admin or above
export const isAdminOrAbove = () => {
  const role = getUserRole();
  return ['superadmin', 'admin'].includes(role);
};
